"use client";

import { useState, useMemo, useEffect, Suspense, useRef } from "react";
import { PageTitleHeader } from "@/components/ui-components/page-title-header";
import { CategoryFilterBar } from "@/components/ui-components/category-filter-bar";
import { LocationCard } from "@/components/ui-components/location-card";
import type { MapLocation, ServiceFilter } from "@/types/location";
import { GoogleMapComponent } from "@/components/ui-components/google-map";
import { useSearchParams, useRouter } from "next/navigation";
import { ArrowDown, ArrowUp, MapPin, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { CONTACT } from "@/lib/constants/contact";
import { useTranslations } from "next-intl";

// Updated sample locations to use new facility types
const sampleLocations: MapLocation[] = [
  {
    id: "miami",
    type: "service-center", // Updated from "car-service"
    name: "Car Care Center Bird Road",
    email: "info@miller40.com",
    phone: CONTACT.phones.primary,
    address: {
      street: "6795 SW 40th St",
      city: "Miami",
      state: "FL",
      country: "United States of America",
      zipCode: "33155",
    },
    position: {
      lat: 25.7327,
      lng: -80.3044,
    },
    services: [
      "Brake repair and replacement",
      "Oil change and routine maintenance",
      "Tire sale and installation",
      "Engine diagnostics and repair",
      "Wheel alignment",
      "Battery replacement",
      "AC heating repair",
    ],
    hours: {
      mondayFriday: "07:00 AM - 06:00 PM",
      saturday: "08:00 AM - 04:00 PM",
      sunday: "Closed",
    },
  },
  {
    id: "calle-8",
    type: "service-center", // Updated from "car-service"
    name: "Calle Ocho Car Care Center",
    email: "calle8@miller40.com",
    phone: CONTACT.phones.calleOcho,
    address: {
      street: "6698 SW 8th St",
      city: "Miami",
      state: "FL",
      country: "United States of America",
      zipCode: "33144",
    },
    position: {
      lat: 25.762962,
      lng: -80.300384,
    },
    services: [
      "Brake repair and replacement",
      "Oil change and routine maintenance",
      "Tire sale and installation",
      "Engine diagnostics and repair",
      "Wheel alignment",
      "Battery replacement",
      "AC heating repair",
    ],
    hours: {
      mondayFriday: "07:00 AM - 06:00 PM",
      saturday: "08:00 AM - 04:00 PM",
      sunday: "Closed",
    },
  },
  {
    id: "collision-center",
    type: "collision-center", // Updated from "collision-repair"
    name: "Collision Repair Center",
    email: "collision@miller40.com",
    phone: CONTACT.phones.collision,
    address: {
      street: "7350 SW 42nd St,",
      city: "Miami",
      state: "FL",
      country: "United States of America",
      zipCode: "33155",
    },
    position: {
      lat: 25.732102,
      lng: -80.309098,
    },
    services: [
      "Auto body repair",
      "Paint matching and repainting",
      "Bumper and fender repair",
      "Frame straightening",
      "Insurance claims assistance",
    ],
    hours: {
      mondayFriday: "07:00 AM - 05:00 PM",
      saturday: "Closed",
      sunday: "Closed",
    },
  },
];

function LocationsPage() {
  const t = useTranslations('locationsPage');
  const tPages = useTranslations('pages.locations');
  const searchParams = useSearchParams();

  // Build filters from translations so labels update with locale
  const facilityFilters: ServiceFilter[] = [
    { id: "all", name: t('filters.all'), slug: "all" },
    { id: "service-center", name: t('filters.serviceCenter'), slug: "service-center" },
    { id: "collision-center", name: t('filters.collisionCenter'), slug: "collision-center" },
  ];
  const router = useRouter();
  const facility = searchParams?.get("facility"); // Changed from "target" to "facility"
  const listRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const [activeFilter, setActiveFilter] = useState(facility || "all");
  const [expandedLocation, setExpandedLocation] = useState<string | null>(
    "miami"
  );

  useEffect(() => {
    setActiveFilter(facility || "all"); // Updated to use facility
  }, [facility]);

  // Filter locations based on selected facility type
  const filteredLocations = useMemo(() => {
    if (activeFilter === "all") {
      return sampleLocations;
    }

    const filterName = facilityFilters.find((f) => f.slug === activeFilter)?.id;

    if (!filterName) {
      return sampleLocations;
    }

    return sampleLocations.filter((location) => location.type === filterName);
  }, [activeFilter]);

  // Ensure expanded location is valid for current filter (but allow null)
  useEffect(() => {
    if (filteredLocations.length > 0 && expandedLocation !== null) {
      const isExpandedLocationInFiltered = filteredLocations.some(loc => loc.id === expandedLocation);

      if (!isExpandedLocationInFiltered) {
        setExpandedLocation(filteredLocations[0].id);
      }
    }
  }, [filteredLocations, expandedLocation]);

  const handleLocationToggle = (locationId: string) => {
    const isCurrentlyExpanded = expandedLocation === locationId;
    const newExpandedLocation = isCurrentlyExpanded ? null : locationId;

    setExpandedLocation(newExpandedLocation);

    // Scroll the expanded location into view if it's being opened
    if (newExpandedLocation && listRef.current) {
      setTimeout(() => {
        const expandedElement = listRef.current?.querySelector(`[data-location-id="${locationId}"]`);
        if (expandedElement) {
          expandedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      }, 100);
    }

    // If closing the expanded location, provide visual feedback
    if (isCurrentlyExpanded && listRef.current) {
      // Smooth scroll to top to show the overview
      setTimeout(() => {
        listRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 200);
    }
  };

  const handleFilterChange = (newFilter: string) => {
    setActiveFilter(newFilter);

    // Reset expanded location to first filtered location
    const newFilterName = facilityFilters.find((f) => f.slug === newFilter)?.id;
    const newFilteredLocations = newFilter === "all"
      ? sampleLocations
      : sampleLocations.filter((location) => location.type === newFilterName);

    // Set expanded location to first in filtered results
    if (newFilteredLocations.length > 0) {
      setExpandedLocation(newFilteredLocations[0].id);
    }

    // Update URL with facility parameter
    const url = new URL(window.location.href);
    if (newFilter === "all") {
      url.searchParams.delete('facility');
    } else {
      url.searchParams.set('facility', newFilter);
    }
    router.replace(url.pathname + url.search, { scroll: false });
  };
  // Check if container is scrollable and scroll position
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const checkScroll = () => {
      const hasVerticalScrollbar = el.scrollHeight > el.clientHeight;
      setIsScrollable(hasVerticalScrollbar);

      if (hasVerticalScrollbar) {
        setCanScrollUp(el.scrollTop > 10); // Small threshold to account for minor scroll positions
        setCanScrollDown(el.scrollTop < el.scrollHeight - el.clientHeight - 10);
      } else {
        setCanScrollUp(false);
        setCanScrollDown(false);
      }
    };

    checkScroll();

    // Add scroll listener to update scroll position indicators
    el.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [filteredLocations, expandedLocation]);

  return (
    <div className="flex flex-col min-h-screen  text-white">
      <main className="flex-1 pt-20">
        <PageTitleHeader
          title={tPages('title')}
          subtitle={tPages('subtitle')}
        />

        <section className="py-16 bg-site">
          <div className="mx-4 md:mx=16 lg:mx-20">
            {/* Filter Description */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold text-white mb-3">{t('chooseServiceTitle')}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                {t('chooseServiceDesc')}
              </p>
            </div>

            <CategoryFilterBar
              filters={facilityFilters}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left side - Location Cards */}
              <div className="relative">
                {/* Location count indicator */}
                <div className="mb-4 text-center">
                  <p className="text-gray-400 text-sm">
                    {t('showing')} {filteredLocations.length} {filteredLocations.length !== 1 ? t('locations') : t('location')}
                    {activeFilter !== "all" && (
                      <span> • <button
                        onClick={() => handleFilterChange("all")}
                        className="text-yellow-500 hover:underline"
                      >
                        Show all locations
                      </button></span>
                    )}
                  </p>
                </div>

                <div
                  ref={listRef}
                  className="space-y-6 md:h-[500px] hide-scrollbar overflow-y-scroll"
                >
                  {filteredLocations.map((location) => (
                    <LocationCard
                      key={location.id}
                      location={location}
                      isExpanded={expandedLocation === location.id}
                      onToggle={() => handleLocationToggle(location.id)}
                      data-location-id={location.id}
                    />
                  ))}
                </div>

                {/* Scroll indicators */}
                {canScrollUp && (
                  <button
                    onClick={() => {
                      listRef.current?.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }}
                    className="absolute hidden md:block top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 rounded-full p-2 text-white hover:bg-opacity-90 transition z-10 shadow-lg"
                    title="Scroll to top"
                  >
                    <ArrowUp className="h-5 w-5" />
                  </button>
                )}

                {canScrollDown && (
                  <button
                    onClick={() => {
                      listRef.current?.scrollTo({
                        top: listRef.current.scrollHeight,
                        behavior: "smooth",
                      });
                    }}
                    className="absolute hidden md:block bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 rounded-full p-2 text-white hover:bg-opacity-90 transition z-10 shadow-lg"
                    title="Scroll to bottom"
                  >
                    <ArrowDown className="h-5 w-5" />
                  </button>
                )}

                {/* Mobile scroll indicator */}
                {isScrollable && (
                  <div className="md:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 rounded-full px-3 py-1 text-white text-xs">
                    {filteredLocations.length} location{filteredLocations.length !== 1 ? 's' : ''} • Scroll for more
                  </div>
                )}
              </div>

              {/* Right side - Map */}
              <div className="lg:sticky lg:top-24 lg:h-fit">
                {/* Map header */}
                <div className="mb-4 text-center">
                  <p className="text-gray-400 text-sm">
                    {expandedLocation ? (
                      <>{t('mapShowing')} <span className="text-yellow-500 font-medium">
                        {filteredLocations.find(loc => loc.id === expandedLocation)?.name}
                      </span></>
                    ) : (
                      <>Select a location to view details and directions</>
                    )}
                  </p>
                </div>

                <div className="h-[500px] rounded-3xl overflow-hidden relative">
                  <div className={cn(
                    "absolute inset-0 transition-all duration-500 ease-in-out",
                    expandedLocation ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
                  )}>
                    {expandedLocation && (
                      <GoogleMapComponent
                        locations={filteredLocations}
                        activeLocationId={expandedLocation}
                      />
                    )}
                  </div>

                  <div className={cn(
                    "absolute inset-0 transition-all duration-500 ease-in-out",
                    !expandedLocation ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
                  )}>
                    {!expandedLocation && (
                      <div className="w-full h-full bg-site-card rounded-3xl flex flex-col items-center justify-center p-8 text-center">
                        <div className="flex flex-col items-center justify-center flex-1">
                          <div className="mb-6">
                            <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4 mx-auto">
                              <MapPin className="h-8 w-8 text-yellow-500" />
                            </div>
                            <h3 className="text-xl font-semibold text-white mb-2">
                              {filteredLocations.length} Miller 40 Location{filteredLocations.length !== 1 ? 's' : ''} Available
                            </h3>
                            <p className="text-gray-400 text-sm max-w-sm mx-auto">
                              Click on any location card to view detailed information, hours, services, and get directions.
                            </p>
                          </div>

                          {/* Quick overview of locations */}
                          <div className="space-y-2 w-full max-w-sm">
                            {filteredLocations.map((location, _index) => (
                              <button
                                key={location.id}
                                onClick={() => setExpandedLocation(location.id)}
                                className="w-full p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors text-left group"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-white font-medium text-sm group-hover:text-yellow-500 transition-colors">
                                      {location.name}
                                    </p>
                                    <p className="text-gray-400 text-xs">
                                      {typeof location.address === 'object'
                                        ? `${location.address.street}, ${location.address.city}`
                                        : location.address}
                                    </p>
                                    <p
                                      className="text-gray-500 text-xs mt-1"
                                      data-phone={location.phone?.key}
                                    >
                                      {location.phone?.display}
                                    </p>
                                  </div>
                                  <ChevronDown className="h-4 w-4 text-gray-400 group-hover:text-yellow-500 transition-colors" />
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default function LocationWrapper() {
  return (
    <Suspense>
      <LocationsPage />
    </Suspense>
  );
}
